import { InvoiceSpedStatusRegistered } from '@/core/domain/invoice/invoices/invoices.enums';
import { SpedDocumentStatus } from '@/core/domain/sped/sped-documents/sped-documents.types';
import { Table, Button, Modal, Form, Col, Input, Row } from 'antd';
import {
  useSpedDocumentContext,
  useSpedDocumentHasItemContext,
} from '@/pages/FiscalPages/SpedPage/SpedDocumentsPage/SpedDocumentDetailPage';
import {
  useSpedDocumentInutilization,
  useSpedDocumentManageImports,
} from '@/core/domain/sped/sped-documents/sped-documents.hooks';
import { useState, useCallback, useEffect } from 'react';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { FloatingFormItem } from '@/components/_common';
import { useDebounce } from '@/hooks';

interface FormValues {
  serie?: string;
  startNumber?: string;
  endNumber?: string;
  model?: string;
  justification?: string;
}

export default function SpedDocumentInutilization() {
  const spedDocument = useSpedDocumentContext();

  const [form] = Form.useForm<FormValues>();

  const formValues = useDebounce(Form.useWatch([], form), 1000);

  const hasRemovePermission = hasPermissions([
    Authorities.SPED_DOCUMENT_IMPORT_INVOICE,
  ]);

  const statusEnabled = [
    SpedDocumentStatus.AWAITING_SELECTION,
    SpedDocumentStatus.ASSESSING,
    SpedDocumentStatus.REOPENED,
    SpedDocumentStatus.REJECTED,
  ];

  const removeDisabled =
    (!!spedDocument?.status && !statusEnabled.includes(spedDocument?.status)) ||
    !hasRemovePermission;

  const [idsSelected, setIdsSelected] = useState<number[]>([]);

  const { data: inutilizations, dataUpdatedAt } = useSpedDocumentInutilization({
    ...formValues,
    spedDocumentId: spedDocument?.id as number,
  });

  const [, handleHasItem] = useSpedDocumentHasItemContext();

  const spedDocumentManageImports = useSpedDocumentManageImports();

  useEffect(() => {
    if (
      inutilizations?.some((item) =>
        [
          InvoiceSpedStatusRegistered.PENDING,
          InvoiceSpedStatusRegistered.ERROR,
        ].includes(item.status),
      )
    ) {
      handleHasItem(true);
    }
  }, [inutilizations, dataUpdatedAt]);

  const removeConfirm = useCallback(async () => {
    Modal.confirm({
      title: 'Remoção de inutilização de numeração importadas',
      content: `Tem certeza que deseja remover as "${idsSelected.length}" seleções de inutilizações de numerações importadas? essa ação não poderá ser revertida`,
      okText: `Remover "${idsSelected.length}"`,
      onOk: async () => {
        await spedDocumentManageImports.mutateAsync({
          importType: 'REMOVE',
          spedDocumentId: spedDocument?.id as number,
          items: idsSelected.map((id) => ({
            entityId: id,
            entityEnum: 'INUTILIZATION',
          })),
        });
        setIdsSelected([]);
      },
      okButtonProps: { danger: true },
    });
  }, [idsSelected]);

  return (
    <>
      <Form form={form}>
        <Row style={{ marginBottom: 24 }} gutter={[16, 16]}>
          <Col span={24} sm={4} md={6}>
            <FloatingFormItem name="serie" label="Série">
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={4} md={6}>
            <FloatingFormItem name="startNumber" label="Numeração Inicial">
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={4} md={6}>
            <FloatingFormItem name="endNumber" label="Numeração Final">
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={4} md={6}>
            <FloatingFormItem name="model" label="Modelo">
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={4} md={6}>
            <FloatingFormItem name="justification" label="Justificativa">
              <Input />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>

      <Table
        dataSource={inutilizations || []}
        rowKey={(item) => item.id}
        rowSelection={{
          selectedRowKeys: idsSelected,
          onChange: (keys) => setIdsSelected(keys as number[]),
          getCheckboxProps: (props) => {
            const disabled =
              props.status === InvoiceSpedStatusRegistered.INSERTED ||
              removeDisabled;
            return { disabled };
          },
        }}
        pagination={false}
        columns={[
          { dataIndex: 'companyName', title: 'Empresa' },
          { dataIndex: 'startNumber', title: 'Numeração inicial' },
          { dataIndex: 'endNumber', title: 'Numeração final' },
          { dataIndex: 'model', title: 'Modelo' },
          { dataIndex: 'serie', title: 'Série' },
          {
            title: 'Justificativa',
            render: (_, item) => item.justification,
            ellipsis: true,
          },
        ]}
        scroll={{ x: true }}
      />

      <div style={{ marginTop: 24 }}>
        <Button
          type="primary"
          danger
          disabled={idsSelected.length < 1 || removeDisabled}
          onClick={removeConfirm}
        >
          {idsSelected.length > 0
            ? `Remover "${idsSelected.length}" ${
                idsSelected.length > 1 ? 'seleções' : 'seleção'
              }`
            : 'Remover seleção'}
        </Button>
      </div>
    </>
  );
}
